/**
 * Rate-limit rejections, in all three shapes the API produces.
 *
 * The shapes do not look alike, and code that only inspects
 * `errors[0].extensions.code` silently misses two of them:
 *
 *   A. Per-operation limit — HTTP **200** with a `TOO_MANY_REQUESTS` entry in
 *      `errors[]`. The 200 is deliberate: a non-2xx would make Apollo classify
 *      it as a transport error and retry into the limit.
 *   B. Global per-IP envelope — HTTP **429** from middleware that runs before
 *      the request body is read. Plain JSON, no `errors[]` at all.
 *   C. REST endpoints (the CSV exports) — HTTP **429**, same body as B.
 *
 * The field names differ between them: `retryAfterSeconds` inside GraphQL
 * extensions, `retryAfter` in the B/C envelope body.
 */

export const TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS";

/** Shown when the server sends no message of its own. */
const FALLBACK_MESSAGE = "Too many requests. Please try again shortly.";

/** What the envelope is called when its body carries no policy name. */
const ENVELOPE_POLICY = "global-ip";

export interface RateLimitRejection {
  message: string;
  retryAfterSeconds?: number;
  /** Tier name. Worth surfacing — limits are retuned by tier, without a deploy. */
  policy?: string;
}

/**
 * The part of `Response` this module needs, so a plain object can stand in for
 * one in tests.
 */
export interface RateLimitedResponse {
  status: number;
  headers?: { get(name: string): string | null };
  json(): Promise<unknown>;
}

type Record_ = { [key: string]: any };

function asRecord(value: unknown): Record_ | null {
  return typeof value === "object" && value !== null ? (value as Record_) : null;
}

function asMessage(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function asPolicy(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

/**
 * Both spellings of the retry hint arrive as untyped JSON, and `Number(null)`
 * is 0 — which would read as "retry immediately" rather than "not stated".
 */
function asRetryAfterSeconds(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;

  const seconds = Number(value);

  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/* ------------------------------------------------------------------ *
 * Shape A — HTTP 200, TOO_MANY_REQUESTS in errors[]
 * ------------------------------------------------------------------ */

function fromGraphQLErrors(error: unknown): RateLimitRejection | null {
  const graphQLErrors = asRecord(error)?.graphQLErrors;

  if (!Array.isArray(graphQLErrors)) return null;

  for (const graphQLError of graphQLErrors) {
    const extensions = asRecord(asRecord(graphQLError)?.extensions);

    if (extensions?.code !== TOO_MANY_REQUESTS) continue;

    return {
      message: asMessage(asRecord(graphQLError)?.message) ?? FALLBACK_MESSAGE,
      retryAfterSeconds: asRetryAfterSeconds(extensions.retryAfterSeconds),
      policy: asPolicy(extensions.policy),
    };
  }

  return null;
}

/* ------------------------------------------------------------------ *
 * Shape B — HTTP 429 with no GraphQL body at all
 * ------------------------------------------------------------------ */

/**
 * The `Retry-After` header, when it is readable.
 *
 * Usually it is not: the API does not list it in the CORS `exposedHeaders`, so
 * browsers strip it before JS can see it. Read as a bonus, never relied on —
 * the body's `retryAfter` is the input that actually arrives.
 */
function headerRetryAfter(headers: unknown): number | undefined {
  const get = asRecord(headers)?.get;

  if (typeof get !== "function") return undefined;

  try {
    return asRetryAfterSeconds(get.call(headers, "retry-after"));
  } catch {
    return undefined;
  }
}

function fromEnvelopeBody(
  body: unknown,
  fallbackRetryAfter?: number
): RateLimitRejection {
  const envelope = asRecord(body);

  return {
    message: asMessage(envelope?.message) ?? FALLBACK_MESSAGE,
    retryAfterSeconds:
      asRetryAfterSeconds(envelope?.retryAfter) ?? fallbackRetryAfter,
    policy: asPolicy(envelope?.policy) ?? ENVELOPE_POLICY,
  };
}

function fromNetworkError(error: unknown): RateLimitRejection | null {
  const record = asRecord(error);

  if (!record) return null;

  // An ApolloError/ErrorResponse wraps it; a bare ServerError is one already.
  const networkError = asRecord(record.networkError) ?? record;

  const status =
    networkError.statusCode ?? asRecord(networkError.response)?.status;

  if (status !== 429) return null;

  // Apollo parses a JSON body onto `result`; a non-JSON body lands on
  // `bodyText` instead and leaves us with just the status.
  return fromEnvelopeBody(
    networkError.result,
    headerRetryAfter(asRecord(networkError.response)?.headers)
  );
}

/**
 * Reads a rate-limit rejection out of an Apollo error, an `onError` response,
 * or a raw network error — covering shapes A and B.
 *
 * Returns `null` for anything else, so callers can fall through to their
 * ordinary error handling.
 */
export function getRateLimitRejection(
  error: unknown
): RateLimitRejection | null {
  return fromGraphQLErrors(error) ?? fromNetworkError(error);
}

/* ------------------------------------------------------------------ *
 * Shape C — a REST 429 from fetch()
 * ------------------------------------------------------------------ */

/**
 * Reads a rate-limit rejection off a `fetch` response, for the CSV exports.
 *
 * Consumes the body, so only call it on a response you are not going to read
 * otherwise. Returns `null` for any status other than 429.
 */
export async function getRateLimitRejectionFromResponse(
  response: RateLimitedResponse
): Promise<RateLimitRejection | null> {
  if (response.status !== 429) return null;

  const retryAfter = headerRetryAfter(response.headers);

  try {
    return fromEnvelopeBody(await response.json(), retryAfter);
  } catch {
    // Rejected before anything JSON-shaped was produced.
    return fromEnvelopeBody(null, retryAfter);
  }
}

/* ------------------------------------------------------------------ *
 * Presentation
 * ------------------------------------------------------------------ */

export function formatRetryAfter(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? "" : "s"}`;
  }

  const minutes = Math.ceil(seconds / 60);

  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * Server messages are already written for a human and already state the wait
 * ("Please try again in 5 minutes."), so they are shown as-is. The tier name is
 * appended because this is the admin app: its users are the people who retune
 * the tier, and the tier name is what the backend asks to be told.
 */
export function describeRateLimitRejection(
  rejection: RateLimitRejection
): string {
  return rejection.policy
    ? `${rejection.message} (limit: ${rejection.policy})`
    : rejection.message;
}

/** Toast-ready message, preferring the retry hint on a rate-limit rejection. */
export function describeError(error: unknown, fallback: string): string {
  const rejection = getRateLimitRejection(error);

  if (rejection) return describeRateLimitRejection(rejection);

  const message = (error as Error | undefined)?.message;

  return message ? `${fallback}: ${message}` : fallback;
}
